// Integration Flows (nav obj_type "J") — an IOFlow flow on the menu, and the
// input form a MANUAL flow is run with (docs/flow_forms.md).
//
// Runtime calls go to /livepreview/flow/* (the running application — project
// users included, through the client's run-as headers); the builder's calls
// (pick a flow, generate / re-sync the form) go to /schedules/*.

import api from './client'
import type { Cadence, TimezoneDrift } from './schedules'

/** One declared input of the flow, normalised (flow_form/variables.py). */
export interface FlowField {
  name: string
  path: string[]
  label: string
  kind: 'scalar' | 'group' | 'table' | 'list' | 'json' | 'file'
  data_type: string
  ui: string
  description: string
  required: boolean
  default: unknown
  validation: Record<string, unknown>
  options: Array<{ value: string; label: string }>
  item_type: string
  multiple: boolean
  children: FlowField[]
}

export interface FlowInfo {
  flow_id: string
  name: string
  description: string
  status: string
  active: boolean
  trigger_type: string
  manual: boolean
  synchronous: boolean
  scheduled: boolean
  cadence: Cadence | null
  cadence_text: string
  next_run: string | null
  next_runs: string[]
  timezone_drift: TimezoneDrift | null
  inputs: FlowField[]
  inputs_hash: string
}

export interface FlowFormStatus {
  exists: boolean
  /** The flow's inputs changed since the form was generated / re-synced. */
  stale: boolean
  notes: string[]
  synced_at?: string
  forms?: number
  fields: number
}

export interface FlowLogLine {
  timestamp: string | null
  level: string
  message: string
  step_id?: string | null
}

export interface FlowRun {
  execution_id: string
  status: string
  final: boolean
  trigger_type: string
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  current_step: string | null
  result?: unknown
  /** IOFlow's declared output format for the result. */
  format?: 'json' | 'html' | 'markdown' | 'text'
  response_status?: number | null
  logs?: FlowLogLine[]
  error?: string
  inputs?: Record<string, unknown> | null
}

export interface FlowMenuInfo {
  success: boolean
  obj_name: string
  flow: FlowInfo
  form: FlowFormStatus
  /** The caller sees every run (they build the app) — else only their own. */
  all_runs: boolean
  /** The caller may generate / re-sync the input form. */
  can_design: boolean
}

export async function getFlowMenu(project: string, objName: string) {
  const res = await api.post('/livepreview/flow/info', { project, obj_name: objName })
  return res.data as FlowMenuInfo
}

export async function listFlowMenuRuns(project: string, objName: string, limit = 40) {
  const res = await api.post('/livepreview/flow/runs',
    { project, obj_name: objName, limit })
  return res.data as { success: boolean; executions: FlowRun[]; all_runs: boolean }
}

export async function getFlowMenuRun(project: string, objName: string, executionId: string) {
  const res = await api.post('/livepreview/flow/run-detail',
    { project, obj_name: objName, execution_id: executionId })
  return res.data as { success: boolean; execution: FlowRun }
}

/** Validate the form with the engine and run the flow. Throws the standard
 *  422 envelope on a validation failure (scope names the failing row). */
export async function runFlowMenu(
  project: string, objName: string,
  body: { header?: Record<string, unknown>; details?: Record<string, unknown>; ignore_warnings?: string[] } = {},
) {
  const res = await api.post('/livepreview/flow/run', {
    project, obj_name: objName,
    header: body.header || {}, details: body.details || {},
    ignore_warnings: body.ignore_warnings || [],
  }, { timeout: 120_000 })
  return res.data as { success: boolean; execution_id: string; flow_id: string; status: string }
}

// ── builder ─────────────────────────────────────────────────────────────────

export interface AvailableFlow {
  flow_id: string
  name: string
  description: string
  status: string
  trigger_type: string
  /** Already recorded as one of this project's flows. */
  project_flow: boolean
}

export async function listAvailableFlows(project: string) {
  const res = await api.post('/schedules/flows/available', { project })
  return res.data as { success: boolean; flows: AvailableFlow[] }
}

export async function createFlowForm(project: string, objName: string, flowId: string) {
  const res = await api.post('/schedules/flow-form/create',
    { project, obj_name: objName, flow_id: flowId }, { timeout: 60_000 })
  return res.data as { success: boolean; flow: FlowInfo; form: FlowFormStatus; created: boolean }
}

export async function syncFlowForm(project: string, objName: string) {
  const res = await api.post('/schedules/flow-form/sync',
    { project, obj_name: objName }, { timeout: 60_000 })
  return res.data as {
    success: boolean; flow: FlowInfo; form: FlowFormStatus
    report: { added: string[]; removed: string[]; refreshed: string[] }
  }
}
